import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { I18nProvider } from '@/contexts/I18nContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { TenantProvider, detectTenantSlug } from '@/contexts/TenantContext';
import GuestApp from '@/pages/GuestApp';
import BookingPortal from '@/pages/BookingPortal';
import AdminPanel from '@/pages/AdminPanel';
import AdminLogin from '@/components/admin/AdminLogin';
import SupplierPortal from '@/pages/SupplierPortal';
import SetupWizard from '@/pages/SetupWizard';
import LandingPage from '@/pages/LandingPage';
import DevelopersPage from '@/pages/DevelopersPage';
import MarketplacePage from '@/pages/MarketplacePage';
import SuperAdminPanel from '@/pages/SuperAdminPanel';
import PricingPage from '@/pages/PricingPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import TermsPage from '@/pages/TermsPage';
import BlogPage from '@/pages/BlogPage';
import BlogArticlePage from '@/pages/BlogArticlePage';
import SitemapPage from '@/pages/SitemapPage';
import VerifyReceiptPage from '@/pages/VerifyReceiptPage';
import { CookieBanner } from '@/components/CookieBanner';
import '@/index.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function AppRouter() {
  const tenantSlug = detectTenantSlug();
  const [setupStatus, setSetupStatus] = useState(null);

  useEffect(() => {
    // Only check setup status when a tenant is active
    if (!tenantSlug) {
      setSetupStatus({ configured: true }); // landing page, no tenant check
      return;
    }
    axios.get(`${BACKEND}/api/setup/status`)
      .then(r => setSetupStatus(r.data))
      .catch(() => setSetupStatus({ configured: true }));
  }, [tenantSlug]);

  // Handler invocato dal SetupWizard al completamento.
  // - Aggiorna subito lo stato locale così il router smette di mostrare il wizard
  // - Persiste il tenant slug in localStorage (safety net per il refresh)
  // - Naviga a /panel/login preservando il parametro ?tenant= (previene la
  //   perdita di contesto tenant su domini come app.shangrilux.com dove il
  //   sottodominio non è indicativo)
  const handleSetupComplete = () => {
    if (tenantSlug) {
      try { localStorage.setItem('tenant_slug', tenantSlug); } catch { /* privacy mode */ }
    }
    setSetupStatus({ configured: true });
    const params = new URLSearchParams(window.location.search);
    const url = new URL('/panel/login', window.location.origin);
    // Mantieni il tenant esplicito se presente (o proveniente dal contesto)
    const slug = params.get('tenant') || tenantSlug;
    if (slug) url.searchParams.set('tenant', slug);
    window.location.replace(url.toString());
  };

  // No tenant detected → show landing page (or superadmin)
  if (!tenantSlug) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookie" element={<CookiePolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticlePage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/verify-receipt" element={<VerifyReceiptPage />} />
        <Route path="/superadmin" element={<SuperAdminPanel />} />
        {/* Route pubbliche / condivise dal cliente: devono funzionare anche
             quando il tenant non viene rilevato via sottodominio (es. app.shangrilux.com).
             Il backend usa il fallback della tenant di default (Shangrilux). */}
        <Route path="/g/:tokenId" element={<GuestApp />} />
        <Route path="/key/:tokenId" element={<GuestApp />} />
        <Route path="/access/:tokenId" element={<GuestApp />} />
        <Route path="/booking" element={<BookingPortal />} />
        <Route path="/booking/:propertySlug" element={<BookingPortal />} />
        <Route path="/fornitore/:token" element={<SupplierPortal />} />
        <Route path="/panel/login" element={<AdminLogin />} />
        <Route path="/panel/*" element={<AdminPanel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Loading setup status for tenant
  if (setupStatus === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--lux-bg)' }}>
        <div className="w-6 h-6 border border-[var(--lux-gold)]/30 border-t-[var(--lux-gold)] rounded-full animate-spin" />
      </div>
    );
  }

  // Tenant not yet configured → Setup Wizard
  if (!setupStatus.configured) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupWizard onComplete={handleSetupComplete} />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  // Tenant configured → full app
  return (
    <Routes>
      <Route path="/" element={<GuestApp />} />
      <Route path="/g/:tokenId" element={<GuestApp />} />
      <Route path="/key/:tokenId" element={<GuestApp />} />
      <Route path="/access/:tokenId" element={<GuestApp />} />
      <Route path="/booking" element={<BookingPortal />} />
      <Route path="/booking/:propertySlug" element={<BookingPortal />} />
      <Route path="/fornitore/:token" element={<SupplierPortal />} />
      <Route path="/panel/login" element={<AdminLogin />} />
      <Route path="/panel/*" element={<AdminPanel />} />
      <Route path="/verify-receipt" element={<VerifyReceiptPage />} />
      <Route path="/setup" element={<Navigate to="/panel" replace />} />
      <Route path="/superadmin" element={<Navigate to="/panel" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <I18nProvider>
          <LanguageProvider>
          <TenantProvider>
            <AuthProvider>
              <BrowserRouter>
                <AppRouter />
                <CookieBanner />
              </BrowserRouter>
              <Toaster
                position="bottom-center"
                toastOptions={{
                  style: {
                    background: 'var(--lux-surface-1)',
                    border: '1px solid var(--lux-gold)',
                    color: 'var(--lux-text)',
                    fontFamily: "'DM Sans', sans-serif",
                  },
                }}
              />
            </AuthProvider>
          </TenantProvider>
        </LanguageProvider>
        </I18nProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
